import { defineStore } from "pinia";
import { useMjmlStore } from "./mjmlStore";
import { getChat } from "@/composables/text-generator/useOpenAiApi";
import { useOpenAiStore } from "./openAiStore";
import { useNotificationsStore } from "./notifications";
import { TopolSectionSchema } from "@/schemas/templateSchema";
import { IOpenAIMessage } from "@/schemas/openAI/openAISchema";
import serializeSections from "@/utils/serializeSections";
import markdownit from "markdown-it";
import { i18nGlobal } from "@/init/i18n-setup";
import parseMjmlToJson from "@/utils/parseMjmlToJson";

interface State {
  enabledChatAI?: boolean;
  enabledChatAIOnSegment?: boolean;
  chat: string;
  contenxtMessage?: IOpenAIMessage;
  promtMessage?: IOpenAIMessage;
  messages: Record<string, IOpenAIMessage[]>;
  loading: Record<string, boolean>;
  loadingSection: boolean;
  isOpenedChat: boolean;
  isNotAtHome: boolean;
  openSettings: boolean;
  errorAnswer: Record<string, boolean>;
  selectedUid?: string;
  lastModelUsed?: string;
  notifyMissingApiKey?: boolean;
}

type SectionItemObject = {
  content: string;
  sections: any;
};

type SuggestionItemObject = {
  content: string;
  suggestions: any;
};

type AnswerArray = Array<
  string | string[] | SectionItemObject | SuggestionItemObject
>;

export type IAnswer = AnswerArray | string;

const relevantBlocks = ["mj-text", "mj-button"];

export const useChatAI = defineStore("chatAI", {
  state: (): State => ({
    enabledChatAI: false,
    enabledChatAIOnSegment: false,
    messages: {
      global: [
        // ...testingMesseages
      ],
    },
    chat: "",
    loading: {
      global: false,
    },
    loadingSection: false,
    isOpenedChat: false,
    isNotAtHome: false,
    openSettings: false,
    errorAnswer: {
      global: false,
    },
    // selectedUid: "GbzUuH8kv",
  }),

  getters: {
    chatAiIsEnable: (state) => {
      const openAiStore = useOpenAiStore();
      if (
        !openAiStore.openAiEnabled &&
        state.enabledChatAI &&
        !state.notifyMissingApiKey
      ) {
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          type: "error",
          text: i18nGlobal.t("layout.chatAI.missing_api_key"),
        });
        state.notifyMissingApiKey = true;
      }
      return state.enabledChatAI && openAiStore.openAiEnabled;
    },
    getMessages: (state) => {
      const messageId = state.selectedUid || "global";
      if (!state.messages[messageId]) {
        state.messages[messageId] = [];
      }
      return state.messages[messageId];
    },
    getLoading: (state) => {
      const messageId = state.selectedUid || "global";
      return state.loading[messageId];
    },
    getErrorAnswer: (state) => {
      const messageId = state.selectedUid || "global";
      return state.errorAnswer[messageId];
    },
  },

  actions: {
    async askAI(question: string, mode?: string) {
      const messageId = this.selectedUid || "global";

      this.errorAnswer[messageId] = false;
      this.loading[messageId] = true;

      if (!this.messages[messageId]) {
        this.messages[messageId] = [];
      }

      this.messages[messageId].push({
        role: "user",
        content: question,
      });

      this.contenxtMessage = this.getChatContext(mode);
      this.lastModelUsed = mode;
      try {
        const answer = await getChat(
          this.contenxtMessage
            ? [this.contenxtMessage].concat(this.messages[messageId])
            : this.messages[messageId],
          this.selectedUid,
          mode
        );

        this.messages[messageId].push(answer);

        this.loading[messageId] = false;

        window.$event.emit("scrollChatToBottom");

        return this.messages;
      } catch (err: any) {
        if (String(err).includes("Error: timeout of")) {
          this.messages[messageId].push({
            role: "assistant",
            content: i18nGlobal.t("layout.chatAI.timeout"),
          });
        } else {
          this.messages[messageId].push({
            role: "assistant",
            content: i18nGlobal.t("layout.chatAI.error"),
          });
        }
        this.loading[messageId] = false;
        this.errorAnswer[messageId] = true;
      }
    },

    enableChatAI(val: boolean, chatAIOnSegment?: boolean) {
      this.enabledChatAI = val;
      if (chatAIOnSegment) {
        this.enabledChatAIOnSegment = chatAIOnSegment;
      }
    },

    getChatContext(mode?: string) {
      const mjmlStore = useMjmlStore();
      const templatePrefix =
        i18nGlobal.t("layout.chatAI.email_template") +
        JSON.stringify(mjmlStore.mjml) +
        "\n";

      let selectedPrefix = "";

      if (mode) {
        selectedPrefix = this.selectedUid
          ? i18nGlobal.t("layout.chatAI.selected_uuid") +
            JSON.stringify(
              mjmlStore.getOnlyTextAndButtonBlocks({
                selectedBlock: mjmlStore.getSegmentByUid(this.selectedUid),
              })
            ) +
            "\n"
          : i18nGlobal.t("layout.chatAI.selected_uuid") +
            JSON.stringify(mjmlStore.getOnlyTextAndButtonBlocks()) +
            "\n";
      } else if (this.selectedUid) {
        selectedPrefix =
          i18nGlobal.t("layout.chatAI.selected_uuid") +
          JSON.stringify(mjmlStore.getSegmentByUid(this.selectedUid)) +
          "\n";
      }

      const prefixMessage = templatePrefix + selectedPrefix;
      if (prefixMessage !== "") {
        return {
          role: "user",
          content: prefixMessage,
        };
      }
      return undefined;
    },

    toggleOpenChat() {
      this.isOpenedChat = !this.isOpenedChat;
      if (!this.isOpenedChat) {
        this.isNotAtHome = false;
        this.selectedUid = undefined;
      }
    },

    closeChat() {
      this.isOpenedChat = false;
      this.selectedUid = undefined;
      this.isNotAtHome = false;
    },

    structurizeChat(text: string) {
      const jsonRespons = JSONResponse(text);
      if (jsonRespons !== null) {
        if (typeof jsonRespons === "string") {
          text = jsonRespons;
        } else {
          return jsonRespons;
        }
      }
      text = replaceMjmlToJson(text);
      const {
        text: newText,
        jsonsSections,
        jsonSuggestions,
      } = replaceJsonToIframe(text);
      text = newText;
      if (jsonSuggestions.length > 0) {
        let suggestionsArray: any = [];
        jsonSuggestions.map((item: any) => {
          if (Array.isArray(item)) {
            suggestionsArray = suggestionsArray.concat(item);
          } else {
            suggestionsArray.push(item);
          }
        });
        if (suggestionsArray.length == 0) {
          return i18nGlobal.t("layout.chatAI.error");
        }
        return {
          content: text,
          suggestions: suggestionsArray,
        };
      }

      const md = markdownit().disable(["code", "table"]);

      const rows = text.split("\n");

      const regex = /^(?:- |[0-9]+\. |[0-9]+\) |\* |").*$/;
      const start = /^(?:- |[0-9]+\. |[0-9]+\) |\* )/;
      let isList = false;
      let hasList = false;
      const textArray: AnswerArray = [];
      let textSubArray: string[] = [];
      let indexOfIfarme = 0;
      let indexOfSuggestion = 0;

      let beforeSpaces = 0;
      let beforeSpycesIndex = 0;
      rows.map((item, index) => {
        const spaces = countLeadingSpaces(item);
        if (spaces > 3 && beforeSpaces === 3) {
          rows[beforeSpycesIndex] = `${rows[beforeSpycesIndex]}
${item}`;
          rows[index] = "";
        } else if (spaces > 3) {
          rows[index] = item.slice(spaces - 3);
        } else {
          beforeSpaces = spaces;
          beforeSpycesIndex = index;
        }
      });

      rows.map((item, index) => {
        if (item.startsWith("<iframe")) {
          hasList = true;
          textArray.push({
            content: item,
            sections: jsonsSections[indexOfIfarme],
          });
          indexOfIfarme++;
          return;
        }
        if (item.startsWith("<suggestion")) {
          hasList = true;
          textArray.push({
            content: item,
            suggestions: jsonSuggestions[indexOfSuggestion],
          });
          indexOfSuggestion++;
          return;
        }
        if (!isList && regex.test(item)) {
          textSubArray.push(md.render(item.replace(start, "")));
          isList = true;
          hasList = true;
        } else if (isList && regex.test(item)) {
          textSubArray.push(md.render(item.replace(start, "")));
          if (index === rows.length - 1) {
            textArray.push(textSubArray);
            isList = false;
            textSubArray = [];
          }
        } else if (isList && !regex.test(item)) {
          textArray.push(textSubArray);
          textArray.push(md.render(item));
          isList = false;
          textSubArray = [];
        } else {
          textArray.push(md.render(item));
        }
      });

      if (hasList && rows.length > 1) {
        return textArray.filter((item) => item !== "");
      } else {
        return md.render(text);
      }
    },
  },
});

function JSONResponse(text: string) {
  try {
    const json = JSON.parse(text);
    if (text.includes("mj-section")) {
      const thisSections = findMjSections(json);
      if (thisSections.length > 0) {
        const secions = TopolSectionSchema.array().safeParse(thisSections);
        if (secions.success) {
          const suggestionsFromSections = getSuggestionsIfExists(secions.data);
          if (suggestionsFromSections) {
            return suggestionsFromSections;
          }
          return [
            {
              content: "<iframe />",
              sections: serializeSections(secions.data),
            },
          ];
        }
      }
    }
    const mjmlStore = useMjmlStore();
    if (Array.isArray(json) && text.includes("suggestion")) {
      const noEmptySuggestions = json.filter(
        (item: any) =>
          item.suggestion !== "" &&
          item.suggestion !== null &&
          item.suggestion !== item.original &&
          relevantBlocks.includes(mjmlStore.getSegmentByUid(item.uid)?.tagName)
      );
      if (noEmptySuggestions.length > 0) {
        return {
          content: JSON.stringify(noEmptySuggestions),
          suggestions: noEmptySuggestions,
        };
      }
    }

    if (
      text.includes("suggestion") &&
      json.suggestion !== "" &&
      json.suggestion !== json.original &&
      relevantBlocks.includes(mjmlStore.getSegmentByUid(json.uid)?.tagName)
    ) {
      return {
        content: JSON.stringify(json),
        suggestions: [json],
      };
    }

    if (typeof json === "string") {
      return null;
    }
    return i18nGlobal.t("layout.chatAI.error");
  } catch (e) {
    return null;
  }
}

function getSuggestionsIfExists(json: any) {
  const mjmlStore = useMjmlStore();
  const suggestions: { uid: string; suggestion: string; comment?: string }[] =
    [];
  for (const section of json) {
    for (const column of section.children) {
      for (const block of column.children) {
        const segment = mjmlStore.getSegmentByUid(block.uid);
        if (segment && relevantBlocks.includes(segment.tagName)) {
          suggestions.push({
            uid: block.uid,
            suggestion: block.content,
            comment: "",
          });
        }
      }
    }
  }
  if (suggestions.length > 0) {
    return {
      content: JSON.stringify(serializeSections(json)),
      suggestions: suggestions,
    };
  }
}

function replaceMjmlToJson(text: string) {
  const mjmlRegex = /```(?:mjml)?[\s\S]*?<mj([\s\S]*?)```/g;
  const mjmlMatches = text.matchAll(mjmlRegex);
  let mjOffset = 0;
  for (const match of mjmlMatches) {
    const json = parseMjmlToJson(
      match[0].slice(match[0].startsWith("```mjml") ? 7 : 3, -3)
    );
    text.replace(match[0], `\`\`\`json\n${JSON.stringify(json)}\n\`\`\``);
    text =
      text.slice(0, match.index - mjOffset) +
      `\`\`\`json\n${JSON.stringify(json)}\n\`\`\`` +
      text.slice(match.index + match[0].length - mjOffset);
    mjOffset +=
      match[0].length - `\`\`\`json\n${JSON.stringify(json)}\n\`\`\``.length;
  }
  return text;
}

function replaceJsonToIframe(text: string) {
  const jsonRegex = /```json([\s\S]*?)```/g;
  const matches = text.matchAll(jsonRegex);
  const jsonsSections: any = [];
  const jsonSuggestions: any = [];
  let offsetIndex = 0;
  for (const match of matches) {
    const jsonString = match[0].slice(7).slice(0, -3);
    if (jsonString.includes("mj-section")) {
      const jsonTemplate = JSON.parse(jsonString);
      const thisSections = findMjSections(jsonTemplate);
      if (thisSections.length > 0) {
        const secions = TopolSectionSchema.array().safeParse(thisSections);
        if (secions.success) {
          jsonsSections.push(serializeSections(secions.data));
        } else {
          jsonsSections.push([]);
        }
      } else {
        jsonsSections.push([]);
      }
      text =
        text.slice(0, match.index - offsetIndex) +
        "\n<iframe />\n" +
        text.slice(match.index + match[0].length - offsetIndex);
      offsetIndex += match[0].length - "\n<iframe />\n".length;
    } else if (jsonString.includes("suggestion")) {
      const mjmlStore = useMjmlStore();
      let suggestionJson = JSON.parse(jsonString);
      if (!Array.isArray(suggestionJson)) {
        suggestionJson = [suggestionJson];
      }
      jsonSuggestions.push(
        suggestionJson.filter(
          (item: any) =>
            item.suggestion &&
            item.suggestion !== item.original &&
            relevantBlocks.includes(
              mjmlStore.getSegmentByUid(item.uid)?.tagName
            )
        )
      );
      text =
        text.slice(0, match.index - offsetIndex) +
        "\n<suggestion />\n" +
        text.slice(match.index + match[0].length - offsetIndex);
      offsetIndex += match[0].length - "\n<suggestion />\n".length;
    } else {
      jsonsSections.push([]);
      text =
        text.slice(0, match.index - offsetIndex) +
        "\n<iframe />\n" +
        text.slice(match.index + match[0].length - offsetIndex);
      offsetIndex += match[0].length - "\n<iframe />\n".length;
    }
  }
  return { text, jsonsSections, jsonSuggestions };
}

function findMjSections(obj: any) {
  const sections: any = [];

  if (Array.isArray(obj)) {
    obj.forEach((item) => sections.push(...findMjSections(item)));
  } else if (obj && typeof obj === "object") {
    if (obj.tagName === "mj-section") {
      sections.push(obj);
    }
    Object.values(obj).forEach((value) =>
      sections.push(...findMjSections(value))
    );
  }
  return sections;
}

function countLeadingSpaces(str: string): number {
  const match = str.match(/^(\s*)/);
  return match ? match[0].length : 0;
}

const testingMesseages = [
  {
    role: "user",
    content: "Do the proof-read, please.",
  },
  {
    role: "assistant",
    content:
      '```json\n[\n    {\n        "uid": "ryZ8KtCt2m",\n        "original": "<p>Write short email preheader</p>",\n        "suggestion": "<p>Write a brief email preheader</p>",\n        "comment": "Clarified language for better readability."\n    },\n    {\n        "uid": "LNfqkfUuF",\n        "original": "<p><span style=\\"color:#000000;\\"><span style=\\"font-size:12px;\\">It doesn&acute;t matter if you are looking for a chair, armchair or couch. We have over 1,000 pieces<br />\\nready for you. The \'seating furniture\' of all shapes and colors is waiting for you.<br />\\nChoose them online or in&nbsp;one of our stores.</span></span></p>",\n        "suggestion": "<p><span style=\\"color:#000000;\\"><span style=\\"font-size:12px;\\">It doesn’t matter whether you are looking for a chair, armchair, or couch. We have over 1,000 pieces<br />\\navailable for you. The seating furniture in all shapes and colors is waiting for you.<br />\\nYou can choose them online or in one of our stores.</span></span></p>",\n        "comment": "Corrected punctuation and improved clarity for better flow."\n    },\n    {\n        "uid": "H1qZUYF0YhX",\n        "original": "<p>Please enter your address and your contact here.</p>\\n\\n<p>Explain why your subscribers are receiving this email.</p>",\n        "suggestion": "<p>Please enter your address and contact information here.</p>\\n\\n<p>Explain why your subscribers are receiving this email.</p>",\n        "comment": "Slightly clarified phrasing for better understanding."\n    }\n]\n```',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "W7PG4EG7O",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 27px;\\"><strong><span style=\\"font-family: Poppins, sans-serif;\\">Beauty Unveiled:</span></strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Poppins, sans-serif; font-size: 20px;\\">Your Guide to Choosing the Right Cosmetics</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 27px;\\"><strong><span style=\\"font-family: Poppins, sans-serif;\\">Beauty Unveiled:</span></strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Poppins, sans-serif; font-size: 20px;\\">Your Guide to Choosing the Right Cosmetics</span></p>",\n        "comment": "No changes were necessary."\n    },\n    {\n        "uid": "bpnopy4n2",\n        "original": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is a nourishing skincare essential that helps hydrate and protect your skin. Rich in moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Perfect for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n        "suggestion": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is an essential skincare product that hydrates and protects your skin. Rich in moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Perfect for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n        "comment": "Changed \'nourishing skincare essential\' to \'essential skincare product\' for better clarity."\n    },\n    {\n        "uid": "hz4S9xrzV",\n        "original": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Lipstick is a versatile beauty product that enhances your look with a splash of color. Whether bold or subtle, it adds definition and completes your makeup. Rich in texture and long-lasting, lipstick is a quick way to express your style and boost confidence.</span></p>",\n        "suggestion": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Lipstick is a versatile beauty product that enhances your look with a splash of color. Whether bold or subtle, it adds definition and completes your makeup. Rich in texture and long-lasting, lipstick is a quick way to express your style and boost your confidence.</span></p>",\n        "comment": "Added \'your\' before \'confidence\' for a more personal touch."\n    },\n    {\n        "uid": "DNYndykjm",\n        "original": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Face cream is a key part of a skincare routine, providing essential moisture and nourishment to the skin. It helps maintain a smooth, hydrated complexion while addressing specific concerns like aging, dullness, or dryness. </span></p>",\n        "suggestion": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Face cream is a key component of a skincare routine, providing essential moisture and nourishment to the skin. It helps maintain a smooth, hydrated complexion while addressing specific concerns such as aging, dullness, or dryness.</span></p>",\n        "comment": "Changed \'part of a skincare routine\' to \'component of a skincare routine\' for improved clarity. Replaced \'like\' with \'such as\'."\n    },\n    {\n        "uid": "2rBjYJRT4",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Poppins, sans-serif;\\">With the right cosmetics, you can enhance your natural beauty and express your unique style.</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Poppins, sans-serif;\\">With the right cosmetics, you can enhance your natural beauty and express your unique style.</span></p>",\n        "comment": "No changes were necessary."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "ntP1IkHK4",\n        "original": "",\n        "suggestion": "",\n        "comment": "As it is, everything looks good to me — I don\'t have any suggestions for changes."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '{\n  "uid": "ntP1IkHK4",\n  "original": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is a nourishing skincare essential that helps hydrate and protect your skin. Rich in moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Perfect for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n  "suggestion": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is an essential skincare product that hydrates and protects your skin. Rich in moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Perfect for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n  "comment": "Replaced \'nourishing skincare essential\' with \'essential skincare product\' for clarity."\n}',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "1FQOTmtxC3",\n        "original": "<p><span style=\\"font-family: Georgia, sans-serif; font-size: 22px;\\"><strong>Silky Smooth: </strong></span></p>\\n<p><strong><span style=\\"font-family: Poppins, sans-serif; font-size: 18px;\\">The Magic of Body Cream</span></strong></p>",\n        "suggestion": "<p><span style=\\"font-family: Georgia, sans-serif; font-size: 22px;\\"><strong>Silky Smooth: </strong></span></p>\\n<p><strong><span style=\\"font-family: Poppins, sans-serif; font-size: 18px;\\">The Magic of Body Cream</span></strong></p>",\n        "comment": "No changes needed; the text is clear and grammatically correct."\n    },\n    {\n        "uid": "zv7bxU4f8",\n        "original": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is a nourishing skincare essential that helps hydrate and protect your skin. Rich in moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Perfect for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n        "suggestion": "<p><span style=\\"font-family: Poppins, sans-serif; font-size: 16px;\\">Body cream is an essential skincare product that hydrates and protects your skin. Packed with moisturizing ingredients, it locks in moisture, leaving your skin soft, smooth, and radiant. Ideal for daily use, it soothes dryness and enhances your natural glow.</span></p>",\n        "comment": "Improved clarity by replacing \'a nourishing skincare essential\' with \'an essential skincare product\'."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "bakjSD21u",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Poppins, sans-serif;\\">With the right cosmetics, you can enhance your natural beauty and express your unique style.</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Poppins, sans-serif;\\">Unlock your beauty potential with our curated cosmetics. Embrace your uniqueness and shine every day!</span></p>",\n        "comment": "The suggestion emphasizes empowerment and excitement, encouraging readers to take action."\n    },\n    {\n        "uid": "Oggiv0F1mb",\n        "original": "<div><span style=\\"font-family: Poppins, sans-serif;\\">SHOP NOW</span></div>",\n        "suggestion": "<div><span style=\\"font-family: Poppins, sans-serif;\\">Discover Your Perfect Look</span></div>",\n        "comment": "The new text is more descriptive and inviting, encouraging potential customers to explore rather than just shop."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '```json\n[\n    {\n        "uid": "7CvmrGI8E",\n        "original": "<p><span style=\\"font-size: 14px;\\">Krásný den,</span></p>\\n<p><span style=\\"font-size: 14px;\\">Vánoce 🎄klepou na dveře a my tušíme, že hledáte něco speciálního pro své blízké. Nebojte se, máme pro vás perfektní dárky, které potěší každého, a ještě je stihneme doručit do Vánoc!</span></p>",\n        "suggestion": "<p><span style=\\"font-size: 14px;\\">Krásný den,</span></p>\\n<p><span style=\\"font-size: 14px;\\">Vánoce 🎄 klepou na dveře a tušíme, že hledáte něco speciálního pro své blízké. Nebojte se, máme pro vás perfektní dárky, které potěší každého a ještě je stihneme doručit do Vánoc!</span></p>",\n        "comment": "Improved grammar and clarity."\n    },\n    {\n        "uid": "TVu2dQNu-",\n        "original": "<p><span style=\\"font-size: 16px;\\">Doručení pod stromeček 🎄 stíhneme při objednání <strong>do půlnoci 16.12.</strong> ✨<strong> Osobní odběry v Třinci </strong>a okolí  možné až do 23.12. do 17 hodin, pak v novém roce 2025 znovu od 6.1.</span></p>",\n        "suggestion": "<p><span style=\\"font-size: 16px;\\">Doručení pod stromeček 🎄 stíháme při objednání <strong>do půlnoci 16.12.</strong> ✨<strong> Osobní odběry v Třinci</strong> a okolí jsou možné až do 23.12. do 17 hodin, poté v novém roce 2025 znovu od 6.1.</span></p>",\n        "comment": "Improved clarity and flow."\n    },\n    {\n        "uid": "KQimNq29t",\n        "original": "<p><span style=\\"font-size: 14px;\\">Objednejte si dárky, které rozzáří oči vašim nejbližším! Milují vymyšlet dárky. Potřebujete poradit? Ozvěte se mi☎️✍🏻. Mějte klidný adventní čas, zkuste si dopřát zastavení, chvilku pro sebe, ztišení a věřte, že máte tolik času, kolik potřebujte. Možná s tímto uvědoměním se vám uleví. To nám všm moc přejí</span></p>\\n<p style=\\"text-align: right;\\"><span style=\\"font-size: 14px;\\">ze ❤️ Sisi.</span></p>",\n        "suggestion": "<p><span style=\\"font-size: 14px;\\">Objednejte si dárky, které rozzáří oči vašim nejbližším! Milují vymýšlet dárky. Potřebujete poradit? Ozvěte se mi☎️✍🏻. Mějte klidný adventní čas, zkuste si dopřát chvilku pro sebe, ztišení a věřte, že máte tolik času, kolik potřebujete. Možná s tímto uvědoměním se vám uleví. To nám všem moc přejí</span></p>\\n<p style=\\"text-align: right;\\"><span style=\\"font-size: 14px;\\">ze ❤️ Sisi.</span></p>",\n        "comment": "Corrected grammar and clarified sentences."\n    }\n]\n```',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "RLjAtGusNa",\n        "original": "<p><span style=\\"font-size: 16px;\\">Doručení pod stromeček 🎄 stíhneme při objednání <strong>do půlnoci 16.12.</strong> ✨<strong> Osobní odběry v Třinci </strong>a okolí  možné až do 23.12. do 17 hodin, pak v novém roce 2025 znovu od 6.1.</span></p>",\n        "suggestion": "<p><span style=\\"font-size: 16px;\\">Objednejte nyní a zajistěte si doručení pod stromeček 🎄! Objednávejte <strong>do půlnoci 16.12.</strong> ✨. Osobní odběry v Třinci a okolí jsou k dispozici až do 23.12. do 17 hodin, pak opět od 6.1. Těšíme se na vaši objednávku!</span></p>",\n        "comment": "The improved version adds urgency and a clear call to action to encourage immediate responses."\n    },\n    {\n        "uid": "KQimNq29t",\n        "original": "<p><span style=\\"font-size: 14px;\\">Objednejte si dárky, které rozzáří oči vašim nejbližším! Milují vymyšlet dárky. Potřebujete poradit? Ozvěte se mi☎️✍🏻. Mějte klidný adventní čas, zkuste si dopřát zastavení, chvilku pro sebe, ztišení a věřte, že máte tolik času, kolik potřebujte. Možná s tímto uvědoměním se vám uleví. To nám všm moc přejí</span></p>",\n        "suggestion": "<p><span style=\\"font-size: 14px;\\">Objednejte dárky, které rozzáří oči vašich blízkých! 🎁 Pokud potřebujete inspiraci, neváhejte se ozvat📞✍🏻. Dopřejte si klidný adventní čas – máte na to, co potřebujete! Věříme, že s našimi dárky přinesete radost.</span></p>",\n        "comment": "The revision encourages readers to take action and reassures them about the support available, fostering a positive emotional connection."\n    }\n]',
  },
  {
    role: "assistant",
    content:
      '"As it is, everything looks good to me — I don\'t have any suggestions for changes."',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '```json\n[\n    {\n        "uid": "71lhAmon1",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px;\\"><strong><span style=\\"font-family: Cabin, sans-serif;\\">*|IF:VOKATIV|*Krásný den,*|VOKATIV|*,*|ELSE:|*Krásný den,*|END:IF|* </span></strong></span></p>\\n<p style=\\"text-align: center;\\"> </p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">jistě se shodneme na tom, že bezpečí našich domovů patří mezi nejdůležitější věci</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">na světě. Proto vás chceme informovat o <strong>čtyřech způsobech, kterými můžete stupeň bezpečí snadno a rychle zvýšit</strong>. Získáte díky nim pocit bezpečí a klidný spánek. 💤</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px;\\"><strong><span style=\\"font-family: Cabin, sans-serif;\\">*|IF:VOKATIV|*Krásný den,*|VOKATIV|*,*|ELSE:|*Krásný den,*|END:IF|* </span></strong></span></p>\\n<p style=\\"text-align: center;\\"> </p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">Společně se shodneme, že bezpečnost našich domovů je jednou z nejdůležitějších věcí</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">na světě. Rádi vám proto představíme <strong>čtyři jednoduché způsoby, jak rychle zvýšit úroveň bezpečnosti</strong>. Díky nim získáte pocit jistoty a klidný spánek. 💤</span></p>",\n        "comment": "Improved clarity and flow for better readability."\n    },\n    {\n        "uid": "FQV6_bW2J6",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px; font-family: Cabin, sans-serif;\\"><strong>Detektor hořlavých plynů 💨</strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Máte obavy z úniku hořlavého plynu v koupelně nebo v kuchyni? Přestaňte se stresovat</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">a pořiďte si detektor, který zachytí i ty nejmenší úniky. V případě nebezpečí<strong> spustí alarm</strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\"><strong>a navíc odešle upozornění přímo na vaše zařízení.</strong> Samotný detektor pak můžete doplnit</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">o praktický uzávěr ventilu, který lze vzdáleně ovládat.</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px; font-family: Cabin, sans-serif;\\"><strong>Detektor hořlavých plynů 💨</strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Máte obavy z úniku hořlavého plynu v koupelně nebo kuchyni? Už se nestresujte</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">a investujte do detektoru, který dokáže zachytit i ty nejmenší úniky. V případě nebezpečí <strong>se spustí alarm</strong></span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\"><strong>a navíc odešle upozornění přímo na vaše zařízení.</strong> Detektor můžete navíc doplnit</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">o praktický uzávěr ventilu, který lze ovládat na dálku.</span></p>",\n        "comment": "Enhanced language for increased persuasion and smoother flow."\n    }\n]\n```',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "dCI-86Evk",\n        "original": "<p style=\\"text-align: right;\\"><span style=\\"text-decoration: underline;\\"><span style=\\"color: #99a0ac; font-size: 12px; text-decoration: underline;\\"><a style=\\"color: #99a0ac; text-decoration: underline;\\" href=\\"*|WEBVERSION|*\\">Nezobrazuje se e-mail správně? Klikněte zde</a></span></span></p>",\n        "suggestion": "<p style=\\"text-align: right;\\"><span style=\\"text-decoration: underline;\\"><span style=\\"color: #99a0ac; font-size: 12px;\\"><a style=\\"color: #99a0ac;\\" href=\\"*|WEBVERSION|*\\">Pokud se e-mail nezobrazuje správně, klikněte zde</a></span></span></p>",\n        "comment": "Rephrased for clearer instruction and to make it sound more natural."\n    },\n    {\n        "uid": "71lhAmon1",\n        "original": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px;\\"><strong><span style=\\"font-family: Cabin, sans-serif;\\">*|IF:VOKATIV|*Krásný den,*|VOKATIV|*,*|ELSE:|*Krásný den,*|END:IF|* </span></strong></span></p>\\n<p style=\\"text-align: center;\\"> </p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">jistě se shodneme na tom, že bezpečí našich domovů patří mezi nejdůležitější věci</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">na světě. Proto vás chceme informovat o <strong>čtyřech způsobech, kterými můžete stupeň bezpečí snadno a rychle zvýšit</strong>. Získáte díky nim pocit bezpečí a klidný spánek. 💤</span></p>",\n        "suggestion": "<p style=\\"text-align: center;\\"><span style=\\"font-size: 18px;\\"><strong><span style=\\"font-family: Cabin, sans-serif;\\">*|IF:VOKATIV|*Krásný den,*|VOKATIV|*,*|ELSE:|*Krásný den,*|END:IF|*</span></strong></span></p>\\n<p style=\\"text-align: center;\\"> </p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">Jsme si jisti, že bezpečnost našich domovů je pro nás všechny velmi důležitá.</span></p>\\n<p style=\\"text-align: center;\\"><span style=\\"font-family: Cabin, sans-serif; font-size: 16px;\\">Proto bychom vám rádi představili <strong>čtyři jednoduché a rychlé způsoby, jak zvýšit úroveň bezpečnosti</strong>. Tyto tipy vám poskytnou klid a pocit jistoty během spánku. 💤</span></p>",\n        "comment": "Enhanced the tone and flow for better readability and engagement."\n    },\n    {\n        "uid": "fU3jtB7bH",\n        "original": "<div><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Prohlížet e-shop</span></div>",\n        "suggestion": "<div><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Prozkoumejte náš e-shop</span></div>",\n        "comment": "Rephrased to sound more inviting and engaging."\n    },\n    {\n        "uid": "C78vu3TVQpU",\n        "original": "<div><span style=\\"font-size: 11px; font-family: Cabin, sans-serif;\\">Odhlásit se z odběru</span></div>",\n        "suggestion": "<div><span style=\\"font-size: 11px; font-family: Cabin, sans-serif;\\">Zrušit odběr</span></div>",\n        "comment": "Shortened for a clearer and more direct call-to-action."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      '[\n    {\n        "uid": "lkzVbZtjz",\n        "original": "<div>Nakupovat</div>",\n        "suggestion": "<div>Zařídíte si bezpečí domova – Nakupujte nyní!</div>",\n        "comment": "Increased emotional appeal and urgency to motivate immediate action."\n    },\n    {\n        "uid": "fU3jtB7bH",\n        "original": "<div><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Prohlížet e-shop</span></div>",\n        "suggestion": "<div><span style=\\"font-size: 16px; font-family: Cabin, sans-serif;\\">Objevte naše produkty pro bezpečí vaší domácnosti!</span></div>",\n        "comment": "Added a benefit-driven message to encourage exploration of products."\n    },\n    {\n        "uid": "C78vu3TVQpU",\n        "original": "<div><span style=\\"font-size: 11px; font-family: Cabin, sans-serif;\\">Odhlásit se z odběru</span></div>",\n        "suggestion": "<div><span style=\\"font-size: 11px; font-family: Cabin, sans-serif;\\">Je nám líto! Odhlásit se můžete zde.</span></div>",\n        "comment": "Made the message more empathetic to reduce negative implications of unsubscribing."\n    }\n]',
    refusal: null,
    annotations: [],
  },
  {
    role: "assistant",
    content:
      'To personalize the email for better engagement, consider the following strategies:\n\n1. **Use Recipient\'s Name**: Start your email with a greeting that includes the recipient\'s name. For example:\n   - Change “Happy Donuts” to “Happy Donuts, [Recipient\'s Name]!”\n\n2. **Segment Your Audience**: Tailor content based on customer segments or preferences. For instance, if you know a segment of your audience prefers certain types of donuts, highlight those options.\n\n3. **Dynamic Content**: Incorporate dynamic content blocks that change based on user data. For example, suggest specific offers based on past purchases.\n\n4. **Location-Based Offers**: If applicable, include offers relevant to the recipient\'s location.\n\n5. **Call to Action**: Tailor your call to action based on the audience\'s previous interactions. Instead of a generic "Check the offer," you can say, "Because you love [specific donut], check out our exclusive offer!"\n\nHere’s an example of personalized greeting and CTA in the email content using MJML format:\n\n```mjml\n<mj-text align="center" padding="8px 80px 0px 80px" font-size="11px">\n    <p style="text-align: center;">\n        <span style="font-size: 48px;">\n            <strong><span style="font-family: Roboto, Tahoma, sans-serif; color: #612c45;">Happy Donuts, [Recipient\'s Name]!</span></strong>\n        </span>\n    </p>\n</mj-text>\n\n<mj-button href="https://topol.io/" align="center" background-color="#fd5973" color="#fff" border-radius="60px" font-size="13px" padding="20px 0px 20px 0px" inner-padding="9px 26px 9px 26px">\n    <strong><span style="font-size: 16px; font-family: Roboto, Tahoma, sans-serif;">Because you love [specific donut], check the exclusive offer!</span></strong>\n</mj-button>\n```\n\nIncorporate these elements to make your emails feel more relevant and engaging to the recipients!',
    refusal: null,
    annotations: [],
  },
];
